import { useState } from "react";
import { useGetList } from "ra-core";
import { subDays } from "date-fns";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { PageHeader } from "../layout/PageHeader";
import {
  type ChangelogAudience,
  type ChangelogEntryRecord,
  type ChangelogType,
} from "./ChangelogEntry";
import { ChangelogAddManualSheet } from "./ChangelogAddManualSheet";
import { ChangelogWeekBlock } from "./ChangelogWeekBlock";
import { isoWeekKey, isoWeekLabel } from "./isoWeek";

// Sprint 9.6 — Refonte /changelog conforme design/atomic-crm/changelog-app.jsx.
// Filtres : type · audience · période. Groupage par semaine ISO via
// ChangelogWeekBlock. Insertion manuelle via AddManualSheet (workflow
// hybride D : cron IA pré-remplit Sprint 9.6b, admin valide ici).

type TypeFilter = ChangelogType | "all";
type PeriodFilter = "all" | "week" | "month" | "quarter";

const TYPE_OPTIONS: { value: TypeFilter; label: string }[] = [
  { value: "all", label: "Tous" },
  { value: "feature", label: "Nouveautés" },
  { value: "improvement", label: "Améliorations" },
  { value: "fix", label: "Correctifs" },
  { value: "breaking", label: "Breaking" },
];

const AUDIENCE_OPTIONS: { value: ChangelogAudience; label: string }[] = [
  { value: "internal", label: "Interne" },
  { value: "external", label: "Public" },
];

const PERIOD_OPTIONS: { value: PeriodFilter; label: string }[] = [
  { value: "all", label: "Tout" },
  { value: "week", label: "Cette semaine" },
  { value: "month", label: "Ce mois" },
  { value: "quarter", label: "Ce trimestre" },
];

const PERIOD_DAYS: Record<PeriodFilter, number | null> = {
  all: null,
  week: 7,
  month: 30,
  quarter: 90,
};

const FilterButton = ({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) => (
  <Button
    type="button"
    size="sm"
    variant={active ? "default" : "outline"}
    className={cn("h-8 text-[12px]", active ? "" : "text-muted-foreground")}
    onClick={onClick}
  >
    {label}
  </Button>
);

export const ChangelogPage = () => {
  const [type, setType] = useState<TypeFilter>("all");
  const [audience, setAudience] = useState<ChangelogAudience>("internal");
  const [period, setPeriod] = useState<PeriodFilter>("all");

  const filter: Record<string, string> = { audience };
  if (type !== "all") {
    filter.type = type;
  }
  const days = PERIOD_DAYS[period];
  if (days != null) {
    filter["released_at@gte"] = subDays(new Date(), days)
      .toISOString()
      .slice(0, 10);
  }

  const { data, isPending } = useGetList<ChangelogEntryRecord>(
    "changelog_entries",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "released_at", order: "DESC" },
      filter,
    },
  );

  // Group by ISO week
  const groups: {
    weekIso: string;
    label: string;
    entries: ChangelogEntryRecord[];
  }[] = [];
  if (data) {
    const byWeek = new Map<string, ChangelogEntryRecord[]>();
    for (const entry of data) {
      const d = new Date(`${entry.released_at}T12:00:00`);
      const key = isoWeekKey(d);
      if (!byWeek.has(key)) byWeek.set(key, []);
      byWeek.get(key)!.push(entry);
    }
    for (const [weekIso, entries] of byWeek.entries()) {
      const first = entries[0];
      const label = isoWeekLabel(new Date(`${first.released_at}T12:00:00`));
      groups.push({ weekIso, label, entries });
    }
    // Already sorted DESC by released_at via query; keep iteration order
  }

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Changelog"
        subtitle="Historique des évolutions du CRM."
      >
        <ChangelogAddManualSheet />
      </PageHeader>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] uppercase tracking-wider text-fabrik-text-3 mr-1">
            Type
          </span>
          {TYPE_OPTIONS.map((opt) => (
            <FilterButton
              key={opt.value}
              active={type === opt.value}
              label={opt.label}
              onClick={() => setType(opt.value)}
            />
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] uppercase tracking-wider text-fabrik-text-3 mr-1">
            Audience
          </span>
          {AUDIENCE_OPTIONS.map((opt) => (
            <FilterButton
              key={opt.value}
              active={audience === opt.value}
              label={opt.label}
              onClick={() => setAudience(opt.value)}
            />
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] uppercase tracking-wider text-fabrik-text-3 mr-1">
            Période
          </span>
          {PERIOD_OPTIONS.map((opt) => (
            <FilterButton
              key={opt.value}
              active={period === opt.value}
              label={opt.label}
              onClick={() => setPeriod(opt.value)}
            />
          ))}
        </div>
      </div>

      {isPending ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : !data?.length ? (
        <div className="rounded-md border border-dashed border-border bg-muted/30 p-6 text-center">
          <p className="text-[13px] text-muted-foreground">
            {audience === "external"
              ? "Aucune note publique pour cette période."
              : "Aucune entrée pour ce filtre."}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          {groups.map((g) => (
            <ChangelogWeekBlock
              key={g.weekIso}
              weekIso={g.weekIso}
              weekLabel={g.label}
              entries={g.entries}
            />
          ))}
        </div>
      )}
    </div>
  );
};

ChangelogPage.path = "/changelog";
